import { useState } from 'react';
import { Alert, Card, Col, Collapse, Descriptions, Empty, Flex, Image, Row, Segmented, Select, Skeleton, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../app/api-context';
import { CalibrationNotice, PhysicalImage } from '../../components/PhysicalSize';
import { CHANGE_LABEL, diffResourceSets } from '../../lib/version-diff';
import type { ResourceSet, VersionCard, VersionList } from '../../lib/version-types';
import { formatBytes } from '../../lib/ops-types';

const RELEASE_REF = 'release:current';
const fmtDate = (s: string | null | undefined) => (s ? new Date(s).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' }) : '정보 없음');
const NONE = <Typography.Text type="secondary">정보 없음</Typography.Text>;

function useResources(ref: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['version-resources', ref],
    queryFn: () => api.get<ResourceSet>(`/versions/resources?${new URLSearchParams({ ref: ref! })}`),
    enabled: !!ref,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

function Thumb({ card, size = 56 }: { card: VersionCard; size?: number }) {
  if (!card.image) {
    return (
      <Flex align="center" justify="center" style={{ width: size, height: size, background: '#fff1f0', border: '1px dashed #ffa39e', flex: 'none' }}>
        <Typography.Text type="danger" style={{ fontSize: 11 }}>없음</Typography.Text>
      </Flex>
    );
  }
  return <Image src={card.image.url} width={size} height={size} style={{ objectFit: 'contain', background: '#fafafa' }} alt={card.text} />;
}

function CardTile({ card, view }: { card: VersionCard; view: 'list' | '3' }) {
  if (view === '3' && card.image) return <PhysicalImage src={card.image.url} alt={card.text} size={3} caption={card.text} />;
  return (
    <Flex gap={8} align="center" style={{ width: 220 }}>
      <Thumb card={card} />
      <div style={{ minWidth: 0 }}>
        <Typography.Text strong ellipsis style={{ display: 'block' }}>{card.text}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }} ellipsis>{card.id}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }} ellipsis>{card.imageResourceId}{card.image ? ` · ${formatBytes(card.image.size)}` : ''}</Typography.Text>
      </div>
    </Flex>
  );
}

function ResourceView({ set }: { set: ResourceSet }) {
  const [view, setView] = useState<'list' | '3'>('list');
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Descriptions size="small" bordered column={{ xs: 1, md: 4 }}>
        <Descriptions.Item label="출처">{set.kind === 'tag' ? `Git 태그 ${set.label}` : 'R2 현재 릴리스'}</Descriptions.Item>
        <Descriptions.Item label="앱 버전">{set.appVersion ? `${set.appVersion}${set.build != null ? ` (빌드 ${set.build})` : ''}` : NONE}</Descriptions.Item>
        <Descriptions.Item label={set.kind === 'tag' ? '태그 커밋 일시' : '릴리스 일시'}>{fmtDate(set.date)}</Descriptions.Item>
        <Descriptions.Item label="기본 카드">{set.cardCount}개</Descriptions.Item>
      </Descriptions>
      {!set.catalog && <Alert type="warning" showIcon message="이 시점에는 cards/cards.catalog.json이 없어 기본 카드 목록을 알 수 없습니다." />}
      {set.missingImages.length > 0 && <Alert type="error" showIcon message={`이미지 파일이 없는 리소스 ${set.missingImages.length}개`} description={set.missingImages.join(', ')} />}
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Segmented value={view} onChange={(v) => setView(v as 'list' | '3')} options={[{ value: 'list', label: '목록' }, { value: '3', label: '3cm' }]} />
        {view === '3' && <CalibrationNotice />}
      </Flex>
      {set.categories.map((cat) => (
        <div key={cat.id}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            {cat.label} <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 'normal' }}>{cat.id} · {cat.cards.length}개</Typography.Text>
          </Typography.Title>
          {cat.cards.length === 0 ? (
            <Typography.Text type="secondary">기본 카드 없음</Typography.Text>
          ) : (
            <Flex wrap gap={12}>
              {cat.cards.map((c) => <CardTile key={c.id} card={c} view={view} />)}
            </Flex>
          )}
        </div>
      ))}
      {set.unreferencedImages.length > 0 && (
        <Collapse
          size="small"
          items={[{
            key: 'unref',
            label: `카탈로그가 참조하지 않는 이미지 파일 ${set.unreferencedImages.length}개`,
            children: (
              <Flex wrap gap={12}>
                {set.unreferencedImages.map((i) => (
                  <Flex key={i.resourceId} vertical align="center" style={{ width: 96 }}>
                    <Image src={i.url} width={56} height={56} style={{ objectFit: 'contain', background: '#fafafa' }} alt={i.resourceId} />
                    <Typography.Text type="secondary" style={{ fontSize: 11 }} ellipsis={{ tooltip: i.resourceId }}>{i.resourceId}</Typography.Text>
                  </Flex>
                ))}
              </Flex>
            ),
          }]}
        />
      )}
    </Space>
  );
}

function CompareView({ base, head }: { base: ResourceSet; head: ResourceSet }) {
  const d = diffResourceSets(base, head);
  const none = !d.added.length && !d.removed.length && !d.changed.length;
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Typography.Text>
        <b>{base.label}</b> → <b>{head.label}</b>: 추가 {d.added.length} · 삭제 {d.removed.length} · 변경 {d.changed.length} · 동일 {d.unchangedCount}
      </Typography.Text>
      {none && <Empty description="기본 카드 차이가 없습니다" />}
      {d.added.length > 0 && (
        <Card size="small" title={<Tag color="green">추가 {d.added.length}</Tag>}>
          <Flex wrap gap={12}>{d.added.map((c) => <CardTile key={c.id} card={c} view="list" />)}</Flex>
        </Card>
      )}
      {d.removed.length > 0 && (
        <Card size="small" title={<Tag color="red">삭제 {d.removed.length}</Tag>}>
          <Flex wrap gap={12}>{d.removed.map((c) => <CardTile key={c.id} card={c} view="list" />)}</Flex>
        </Card>
      )}
      {d.changed.length > 0 && (
        <Card size="small" title={<Tag color="gold">변경 {d.changed.length}</Tag>}>
          <Table
            size="small"
            pagination={false}
            rowKey={(r) => r.after.id}
            dataSource={d.changed}
            columns={[
              { title: '카드 ID', render: (_, r) => <Typography.Text code>{r.after.id}</Typography.Text> },
              { title: '변경', render: (_, r) => r.changes.map((c) => <Tag key={c}>{CHANGE_LABEL[c]}</Tag>) },
              { title: base.label, render: (_, r) => <Flex gap={8} align="center"><Thumb card={r.before} size={48} />{r.before.text}</Flex> },
              { title: head.label, render: (_, r) => <Flex gap={8} align="center"><Thumb card={r.after} size={48} />{r.after.text}</Flex> },
            ]}
          />
        </Card>
      )}
    </Space>
  );
}

function LatestCard({ list }: { list: VersionList }) {
  const l = list.latest;
  return (
    <Card title="현재 latest.json (R2)" size="small">
      {!list.r2Configured ? (
        <Alert type="warning" showIcon message="R2 조회 자격증명이 설정되지 않았습니다." />
      ) : !l ? (
        <Alert type="warning" showIcon message="latest.json을 찾지 못했습니다." />
      ) : (
        <Descriptions size="small" column={1}>
          <Descriptions.Item label="현재 릴리스">{l.releaseId ?? NONE}</Descriptions.Item>
          <Descriptions.Item label={<Tooltip title="앱이 업데이트 안내에 쓰는 값입니다. 스토어 배포 상태를 조회한 값이 아닙니다.">스토어 공개 버전 포인터</Tooltip>}>{l.latestAppVersion || NONE}</Descriptions.Item>
          <Descriptions.Item label="카드 카탈로그">{l.cardsManifest ? <Typography.Text code>{l.cardsManifest}</Typography.Text> : NONE}</Descriptions.Item>
          <Descriptions.Item label="리소스 목록">{l.resourcesManifest ? <Typography.Text code>{l.resourcesManifest}</Typography.Text> : NONE}</Descriptions.Item>
          <Descriptions.Item label="Android 스토어">{l.androidStoreUrl ? <a href={l.androidStoreUrl} target="_blank" rel="noreferrer">링크</a> : NONE}</Descriptions.Item>
          <Descriptions.Item label="iOS 스토어">{l.iosStoreUrl ? <a href={l.iosStoreUrl} target="_blank" rel="noreferrer">링크</a> : NONE}</Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
}

export function VersionsPage() {
  const api = useApi();
  const list = useQuery({ queryKey: ['versions'], queryFn: () => api.get<VersionList>('/versions'), staleTime: 5 * 60_000, retry: false });
  const [picked, setPicked] = useState<string | null>(null);
  const [compareTo, setCompareTo] = useState<string | null>(null);

  const data = list.data;
  const refs = [
    ...(data?.tags ?? []).map((t) => ({ value: `tag:${t.name}`, label: `${t.name}${t.build != null ? ` (빌드 ${t.build})` : ''}` })),
    ...(data?.r2Configured && data.latest?.releaseId ? [{ value: RELEASE_REF, label: `현재 배포 릴리스 ${data.latest.releaseId}` }] : []),
  ];
  const selected = picked ?? refs[0]?.value ?? null;
  // 클수록 최신. 태그는 최신순으로 정렬되어 오고, 현재 릴리스는 가장 최신으로 본다.
  const tagCount = data?.tags.length ?? 0;
  const recency = (ref: string) => (ref === RELEASE_REF ? Number.MAX_SAFE_INTEGER : tagCount - refs.findIndex((r) => r.value === ref));
  const main = useResources(selected);
  const other = useResources(compareTo && compareTo !== selected ? compareTo : null);

  return (
    <>
      <Typography.Title level={3}>버전별 리소스</Typography.Title>
      <Typography.Paragraph type="secondary">
        아이소통카드 Git 태그 시점의 카드 카탈로그와 이미지 파일, R2 현재 릴리스를 보여줍니다. 스토어 배포 상태와 태그가 없는 버전의 리소스는 출처가 없어 표시하지 않습니다.
      </Typography.Paragraph>

      {list.isPending && <Skeleton active />}
      {list.isError && <Alert type="error" showIcon message="버전 정보를 불러오지 못했습니다" description={(list.error as Error).message} />}
      {data && (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card title="앱 버전" size="small">
              {!data.repoConfigured && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="앱 저장소 읽기 토큰이 설정되지 않아 Git 태그를 조회할 수 없습니다." />}
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={[
                  ...data.tags.map((t) => ({ key: `tag:${t.name}`, version: t.appVersion ?? t.name, tag: t.name, build: t.build != null ? String(t.build) : null, date: fmtDate(t.date), resources: true })),
                  ...data.untagged.map((u) => ({
                    key: `untagged:${u.version}`,
                    version: u.version,
                    tag: null,
                    build: u.minBuild != null ? (u.minBuild === u.maxBuild ? String(u.minBuild) : `${u.minBuild}~${u.maxBuild}`) : null,
                    date: `${fmtDate(u.firstDate)} ~ ${fmtDate(u.lastDate)} (pubspec 기록)`,
                    resources: false,
                  })),
                ].sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true }))}
                columns={[
                  { title: '버전', dataIndex: 'version' },
                  { title: 'Git 태그', dataIndex: 'tag', render: (v: string | null) => (v ? <Tag color="blue">{v}</Tag> : <Tooltip title="이 버전을 가리키는 Git 태그가 없어 배포 시점과 기본 리소스를 알 수 없습니다."><Tag>태그 없음</Tag></Tooltip>) },
                  { title: '빌드', dataIndex: 'build', render: (v: string | null) => v ?? NONE },
                  { title: '일시', dataIndex: 'date' },
                  { title: '기본 리소스', dataIndex: 'resources', render: (v: boolean) => (v ? '조회 가능' : NONE) },
                ]}
              />
              {data.historyLimited && <Typography.Text type="secondary" style={{ fontSize: 12 }}>pubspec 기록은 최근 200개 커밋까지만 확인했습니다.</Typography.Text>}
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <LatestCard list={data} />
          </Col>

          <Col span={24}>
            <Card
              size="small"
              title="기본 리소스"
              extra={
                refs.length > 0 && (
                  <Flex gap={8} wrap>
                    <Select value={selected} onChange={setPicked} options={refs} style={{ minWidth: 200 }} />
                    <Select
                      allowClear
                      placeholder="비교할 버전"
                      value={compareTo}
                      onChange={(v) => setCompareTo(v ?? null)}
                      options={refs.filter((r) => r.value !== selected)}
                      style={{ minWidth: 200 }}
                    />
                  </Flex>
                )
              }
            >
              {refs.length === 0 && <Empty description="조회할 버전이 없습니다" />}
              {main.isPending && selected && <Skeleton active />}
              {main.isError && <Alert type="error" showIcon message="기본 리소스를 불러오지 못했습니다" description={(main.error as Error).message} />}
              {main.data && compareTo && compareTo !== selected ? (
                other.isPending ? <Skeleton active /> : other.isError ? (
                  <Alert type="error" showIcon message="비교할 버전을 불러오지 못했습니다" description={(other.error as Error).message} />
                ) : other.data ? (
                  // 비교는 항상 과거 → 최신 방향으로 보여준다
                  recency(compareTo) < recency(selected!)
                    ? <CompareView base={other.data} head={main.data} />
                    : <CompareView base={main.data} head={other.data} />
                ) : null
              ) : (
                main.data && <ResourceView set={main.data} />
              )}
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}
